import type { ExamStatus } from '@dru/types';

/**
 * The journey timeline.
 *
 * The most important component in the app, because it answers the only
 * question a worker actually has: where is my checkup?
 *
 * Fifteen internal states collapse into six human steps. A worker does not
 * need to know the difference between LAB_PENDING and REVIEW_PENDING — they
 * need to know somebody is still working on it. The internal state stays in
 * the API, where the state machine needs it.
 */

const STEPS = [
  'Requested', 'Appointment booked', 'Checked in', 'Tests', 'Doctor review', 'Completed',
] as const;

/** Which human step a machine state belongs to. */
function stepIndex(status: ExamStatus): number {
  switch (status) {
    case 'DRAFT': case 'REQUESTED': case 'ROUTED': case 'CONSENT_PENDING': return 0;
    case 'SCHEDULED': case 'NO_SHOW_OPEN': case 'NO_SHOW_CLOSED': return 1;
    case 'CHECKED_IN': return 2;
    case 'IN_PROGRESS': case 'LAB_PENDING': return 3;
    case 'REVIEW_PENDING': case 'RETEST_REQUIRED': return 4;
    case 'CERTIFIED': return 5;
    default: return 0;
  }
}

/**
 * What the current step means, and what — if anything — the worker does next.
 *
 * "You are here" said where, never what: a worker whose samples were at the
 * laboratory had no way to know their results would appear under Reports, or
 * that a doctor still had to look at them before a certificate existed.
 */
function currentLine(status: ExamStatus): string {
  switch (status) {
    case 'CONSENT_PENDING': return 'Waiting for your consent before it can go ahead.';
    case 'DRAFT': case 'REQUESTED': case 'ROUTED':
      return 'Your employer has asked for this checkup. You will be told the date and place.';
    case 'SCHEDULED': return 'Your appointment is booked. Carry a photo ID.';
    case 'NO_SHOW_OPEN': case 'NO_SHOW_CLOSED':
      return 'You missed your appointment. Your employer will arrange a new one.';
    case 'CHECKED_IN': return 'You have been checked in at the desk. Your tests are next.';
    case 'IN_PROGRESS': return 'Your tests are being done.';
    case 'LAB_PENDING':
      return 'Your samples are being tested. Results appear under Reports once they are checked.';
    case 'REVIEW_PENDING':
      return 'A doctor is checking your results. Your fitness certificate comes next.';
    case 'RETEST_REQUIRED': return 'A test needs to be repeated. Your employer will arrange it.';
    case 'CERTIFIED': return 'Your fitness certificate is ready under Fitness.';
    default: return 'In progress.';
  }
}

export function Journey({ status }: { status: ExamStatus }) {
  const current = stepIndex(status);
  const stalled = status === 'NO_SHOW_OPEN' || status === 'RETEST_REQUIRED';

  return (
    <ol className="space-y-0" aria-label="Checkup progress">
      {STEPS.map((label, i) => {
        const done = i < current || (status === 'CERTIFIED' && i === current);
        const active = i === current && status !== 'CERTIFIED';

        return (
          <li key={label} className="flex gap-3" aria-current={i === current ? 'step' : undefined}>
            <div className="flex flex-col items-center">
              <span
                aria-hidden
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
                  done ? 'bg-covered-600 text-white'
                  : active ? (stalled ? 'bg-dueSoon-600 text-white' : 'bg-primary-600 text-white')
                  : 'border border-neutral-300 bg-white text-neutral-500'
                }`}
              >
                {done ? '✓' : active && stalled ? '!' : i + 1}
              </span>
              {i < STEPS.length - 1 && (
                <span className={`w-0.5 flex-1 ${done ? 'bg-covered-600' : 'bg-neutral-200'}`} />
              )}
            </div>

            <div className={`min-w-0 pb-5 ${i === STEPS.length - 1 ? 'pb-0' : ''}`}>
              {/* Never colour alone: every step carries its label, a done step
                  says so to a screen reader, and the current one says what is
                  happening in words. */}
              <p className={`text-sm ${
                active || (done && i === current) ? 'font-semibold text-neutral-900'
                : done ? 'text-neutral-700' : 'text-neutral-500'
              }`}>
                {label}
                {done && <span className="sr-only"> (done)</span>}
                {!done && !active && <span className="sr-only"> (not yet)</span>}
              </p>
              {i === current && (
                <p className={`mt-0.5 text-xs ${stalled ? 'font-medium text-dueSoon-600' : 'text-neutral-600'}`}>
                  {currentLine(status)}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
